import React, { useEffect, useState } from 'react';
import { TCarouselListingItem } from 'types/TCarousels';
import { useI18nContext } from '@ecg-marktplaats/js-react-i18n';
import { VEHICLE_TYPE_CATEGORY_IDS_NUM, VEHICLE_TYPES } from '../../../../../constants/vehicleTypes';

type RecentlyViewedListProps = {
  favoriteAdItems: TCarouselListingItem[];
  onFavClickHandler: (item: TCarouselListingItem, isFavorite: boolean) => Promise<void>;
  vehicleType: string;
  isLease: boolean;
};

const LIST_TYPE = 'recently_viewed';
const RECENTLY_VIEWED_VIPS_STORAGE_KEY = 'RecentlyViewedVips';
const SHUFFLE_PARAM = 0.5;
let ListingItemCarousel;
let buildCarsCarouselItemModel;
let checkListingAvailability;

const RecentlyViewedList = ({ favoriteAdItems, onFavClickHandler, vehicleType, isLease }: RecentlyViewedListProps) => {
  const { t } = useI18nContext();
  const [recentlyViewedItems, setRecentlyViewedItems] = useState<TCarouselListingItem[]>([]);
  const [relevantRecentItems, setRelevantRecentItems] = useState<TCarouselListingItem[]>([]);
  const [refetchedListings, setRefetchedListings] = useState<string[]>([]);
  const [listingItemCarouselLoaded, setListingItemCarouselLoaded] = useState<boolean>(false);

  useEffect(() => {
    void (async () => {
      ListingItemCarousel = (
        await import(/* webpackChunkName: "ListingItemCarousel" */ '../../Carousels/components/ListingItemCarousel')
      ).default;
      setListingItemCarouselLoaded(true);
    })();
  }, []);

  useEffect(() => {
    setRelevantRecentItems(
      recentlyViewedItems.filter((item) => {
        const matchesL2Category =
          vehicleType === VEHICLE_TYPES.CARS
            ? !Object.values(VEHICLE_TYPE_CATEGORY_IDS_NUM).includes(item.l2CategoryId)
            : item.l2CategoryId === VEHICLE_TYPE_CATEGORY_IDS_NUM[vehicleType];
        const matchesPriceType = isLease ? item.isLeaseCar : !item.isLeaseCar;
        return matchesL2Category && matchesPriceType;
      }),
    );
  }, [recentlyViewedItems, vehicleType, isLease]);

  const onErrorHandler = async (erroredItem: TCarouselListingItem) => {
    if (refetchedListings.indexOf(erroredItem.itemId) >= 0) {
      return;
    }
    refetchedListings.push(erroredItem.itemId);
    setRefetchedListings(refetchedListings);

    try {
      const itemsStringValue = localStorage.getItem(RECENTLY_VIEWED_VIPS_STORAGE_KEY);
      const items = itemsStringValue ? JSON.parse(itemsStringValue) : [];

      if (items.length) {
        checkListingAvailability = (
          await import(
            /* webpackChunkName: "checkListingAvailability" */ '../../Carousels/utils/checkListingAvailability'
          )
        ).default;
        const availableListings = await checkListingAvailability([erroredItem], items);
        localStorage.setItem(RECENTLY_VIEWED_VIPS_STORAGE_KEY, JSON.stringify(availableListings));

        buildCarsCarouselItemModel = (
          await import(/* webpackChunkName: "buildCarsCarouselItemModel" */ '../../Carousels/models/carouselItemModel')
        ).default;

        setRecentlyViewedItems(buildCarsCarouselItemModel(availableListings));
      }
    } catch {}
  };

  useEffect(() => {
    void (async () => {
      try {
        const itemsStringValue = localStorage.getItem(RECENTLY_VIEWED_VIPS_STORAGE_KEY);
        const items = itemsStringValue ? JSON.parse(itemsStringValue) : [];

        if (items.length) {
          let carsItems;
          try {
            buildCarsCarouselItemModel = (
              await import(
                /* webpackChunkName: "buildCarsCarouselItemModel" */ '../../Carousels/models/carouselItemModel'
              )
            ).default;
            carsItems = buildCarsCarouselItemModel(items);
          } catch {}

          if (carsItems.length) {
            try {
              ListingItemCarousel = (
                await import(
                  /* webpackChunkName: "ListingItemCarousel" */ '../../Carousels/components/ListingItemCarousel'
                )
              ).default;
              setListingItemCarouselLoaded(true);
              setRecentlyViewedItems(carsItems);
            } catch {}

            const shuffledCarsItems = [...carsItems].toSorted(() => SHUFFLE_PARAM - Math.random());
            checkListingAvailability = (
              await import(
                /* webpackChunkName: "checkListingAvailability" */ '../../Carousels/utils/checkListingAvailability'
              )
            ).default;
            const availableListings = await checkListingAvailability(shuffledCarsItems, items);
            localStorage.setItem(RECENTLY_VIEWED_VIPS_STORAGE_KEY, JSON.stringify(availableListings));
            buildCarsCarouselItemModel = (
              await import(
                /* webpackChunkName: "buildCarsCarouselItemModel" */ '../../Carousels/models/carouselItemModel'
              )
            ).default;
            setRecentlyViewedItems(buildCarsCarouselItemModel(availableListings));
          }
        }
      } catch {}
    })();
  }, []);

  if (!relevantRecentItems.length || !listingItemCarouselLoaded) {
    return null;
  }

  return (
    <ListingItemCarousel
      type={LIST_TYPE}
      title={t(`cars.carousel.title.${LIST_TYPE}`)}
      items={relevantRecentItems}
      favoriteAdIdList={favoriteAdItems.map(({ itemId }) => itemId)}
      onFavClickHandler={onFavClickHandler}
      onErrorHandler={onErrorHandler}
    />
  );
};

export default RecentlyViewedList;
